import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import {motion} from 'framer-motion';
import Image from 'next/image';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {ModuleMargin} from 'Frontend/components/ModuleMargins';

import {Content} from 'Frontend/assets/images/icons';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, iconAsBackground, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA, MEDIA_JS} from 'Frontend/utils/mediaQueries';

const Index = {
    open: {height: 'auto'},
    close(media) {
        if (media === 'Mobile') {
            return {height: '0px'};
        }

        return {height: 'auto'};
    }
};

/**
 * PageIndex
 *
 * @property {*} prop Prop comment.
 *
 * @class PageIndex
 * @extends {Component}
 */
class PageIndex extends Component {
    static propTypes = {
        image: PropTypes.object.isRequired,
        links: PropTypes.arrayOf(
            PropTypes.shape({
                hash: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                // eslint-disable-next-line @nfq/sort-keys
                children: PropTypes.arrayOf(PropTypes.shape({
                    hash: PropTypes.string.isRequired,
                    name: PropTypes.string.isRequired
                }))
            })
        ).isRequired,
        isInGroup: PropTypes.bool
    }

    static defaultProps = {isInGroup: false}

    /**
     * Creates an instance of PageIndex.
     *
     * @param {Object} props Component props.
     * @memberof PageIndex
     */
    constructor(props) {
        super(props);

        if (process.browser) {
            this.mobile = window.matchMedia(MEDIA_JS.MOBILE.DOWN);
        }

        this.state = {
            open: false,
            viewport: 'Desktop'
        };
    }

    /**
     * Add listener to viewport changes.
     *
     * @memberof ImageModule
     */
    componentDidMount() {
        if ('addEventListener' in this.mobile) {
            this.mobile.addEventListener('change', this.setViewport);
        } else {
            this.mobile.addListener(this.setViewport);
        }

        this.setViewport();
    }

    /**
     * Remove listener to viewport changes.
     *
     * @memberof ImageModule
     */
    componentWillUnmount() {
        if ('removeEventListener' in this.mobile) {
            this.mobile.removeEventListener('change', this.setViewport);
        } else {
            this.mobile.removeListener(this.setViewport);
        }
    }

    /**
     * Sets the viewport in state.
     *
     * @memberof ImageModule
     */
    @autobind
    setViewport() {
        this.setState({viewport: this.getViewport()});
    }

    /**
     * Gets the viewport from the mediaQuerySelector.
     *
     * @returns {String} The current viewport.
     * @memberof ImageModule
     */
    @autobind
    getViewport() {
        if (this.mobile.matches) {
            return 'Mobile';
        }

        return 'Desktop';
    }

    /**
     * Handles the index menu opening.
     *
     * @memberof PageIndex
     */
    @autobind
    handleMenuOpen() {
        this.setState(oldState => ({open: !oldState.open}));
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof PageIndex
     */
    render() {
        const {open, viewport} = this.state;
        const {image: {alt, dimensions: {height, width}, publicPath, title}, isInGroup, links} = this.props;

        return (
            <PageIndexElement isInGroup={isInGroup}>
                <ImageContainer>
                    <Image
                        alt={alt}
                        height={height}
                        layout="responsive"
                        src={publicPath.src}
                        title={title}
                        width={width}
                    />
                </ImageContainer>
                <PageIndexContainer>
                    <AccordionHead onClick={this.handleMenuOpen}><StyledContent /> Inhalt</AccordionHead>
                    <AccordionBody
                        animate={open ? 'open' : 'close'}
                        custom={viewport}
                        initial="close"
                        variants={Index}
                    >
                        <LinkList>
                            {links.map(item => {
                                const {children, hash, name} = item;

                                return (
                                    <LinkItem key={hash}>
                                        <Link href={`#${hash}`}>{name}</Link>
                                        {children && (
                                            <LinkList second>
                                                {children.map(child => {
                                                    const {hash: subHash, name: subName} = child;

                                                    return (
                                                        <LinkItem key={subHash} second>
                                                            <SubLink href={`#${subHash}`}>{subName}</SubLink>
                                                        </LinkItem>
                                                    );
                                                })}
                                            </LinkList>
                                        )}
                                    </LinkItem>
                                );
                            })}
                        </LinkList>
                    </AccordionBody>
                </PageIndexContainer>
            </PageIndexElement>
        );
    }
}

export default PageIndex;

const PageIndexElement = styled(ModuleMargin)`
    align-items: center;
    background-color: ${({isInGroup}) => (isInGroup ? '#ffffff' : 'transparent')};
    display: flex;
    flex-direction: column-reverse;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    width: 100%;

    ${({isInGroup}) => (isInGroup ? `&& {margin-top: ${clampCss(-128, 'm', true)};}` : null)};

    ${MEDIA.TABLET.UP} {
        background-color: transparent;
        flex-direction: row-reverse;
        padding: 0 0 0 ${clampCss(57.594, 't')};

        && {margin-top: 0}
    }

    ${MEDIA.DESKTOP.UP} {
        flex-direction: row-reverse;
        margin-left: calc(50% + max(124px, ${pxToVw(124, 'd')}));
        margin-right: max(-124px, ${pxToVw(-124, 'd', true)});
        max-width: 100%;
        padding: 0;
        transform: translateX(-50%);
        width: ${({isInGroup}) => (isInGroup ? '100%' : `calc(max(1320px, ${pxToVw(1320, 'd')}) - max(251px, ${pxToVw(251, 'd')}))`)};

        && {margin-top: 0}
    }
`;

const ImageContainer = styled.div`
    width: 100%;

    ${MEDIA.TABLET.UP} {
        align-self: flex-start;
        margin-left: 32px;
        margin-right: 0;
        width: calc(60% - 16px);
    }
`;

const PageIndexContainer = styled.div`
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.TABLET.UP} {
        width: calc(40% - 16px);
    }
`;

const AccordionHead = styled.div`
    ${FONTS.SEMIBOLD}
    border-bottom: 1px solid ${({theme}) => theme.colors.textColor};
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    padding-bottom: ${clampCss(15.5, 'm')};

    ${MEDIA.TABLET.UP} {
        border-bottom: none;
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        padding-bottom: ${clampCss(24, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        padding-bottom: max(24px, ${pxToVw(24, 'd')});
    }
`;

const StyledContent = styled.div`
    background-image: url(${iconAsBackground(Content)});
    background-position: center center;
    background-repeat: no-repeat;
    background-size: 100% 100%;
    display: inline-block;
    height: ${clampCss(12, 'm')};
    margin-right: ${clampCss(8, 'm')};
    position: relative;
    top: ${clampCss(1, 'm')};
    width: ${clampCss(12, 'm')};

    ${MEDIA.TABLET.UP} {
        height: ${clampCss(16, 't')};
        margin-right: ${clampCss(8, 't')};
        top: ${clampCss(3, 't')};
        width: ${clampCss(16, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        height: max(16px, ${pxToVw(16, 'd')});
        margin-right: max(8px, ${pxToVw(8, 'd')});
        top: max(3px, ${pxToVw(3, 'd')});
        width: max(16px, ${pxToVw(16, 'd')});
    }
`;

const AccordionBody = styled(motion.div)`
    height: 0px;
    margin-bottom: ${clampCss(23, 'm')};
    overflow: hidden;

    ${MEDIA.TABLET.UP} {
        height: auto;
        margin-bottom: 0;
    }
`;

const LinkList = styled.ul`
    border: ${({second}) => (second ? 'none' : '1px solid #C8C8D0')};
    list-style: none;
    margin: 0;
    padding: ${({second}) => (second ? '0' : `${clampCss(5.5, 'm')} ${clampCss(24, 'm')} ${clampCss(5.5, 'm')} ${clampCss(24, 'm')}`)};

    ${MEDIA.TABLET.UP} {
        border: none;
        margin: 0;
        padding: ${({second}) => (second ? `${clampCss(8, 't')} 0 0 0` : 0)};
    }

    ${MEDIA.DESKTOP.UP} {
        padding: ${({second}) => (second ? `max(8px, ${pxToVw(8, 'd')}) 0 0 0` : 0)};
    }
`;

const LinkItem = styled.li`
    cursor: pointer;
    padding: ${clampCss(8, 'm')} 0 ${clampCss(8, 'm')} ${({second}) => (second ? clampCss(14, 'm') : '0')};
    position: relative;

    ${MEDIA.TABLET.UP} {
        padding: ${clampCss(8, 't')} 0 ${clampCss(8, 't')} ${({second}) => (second ? clampCss(28, 't') : clampCss(14, 't'))};
    }

    ${MEDIA.DESKTOP.UP} {
        padding: max(8px, ${pxToVw(8, 'd')}) 0 max(8px, ${pxToVw(8, 'd')}) ${({second}) => (second ? `max(14px, ${pxToVw(28, 'd')})` : `max(14px, ${pxToVw(14, 'd')})`)};
    }
`;

const Link = styled.a`
    ${FONTS.SEMIBOLD}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    position: relative;
    text-decoration: none;

    &:hover {
        color: #D56B50;

        &::after {
            background-color: #D56B50;
        }
    }

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};

        &::after {
            background-color: #C8C8D0;
            border-radius: 50%;
            content: '';
            height: ${clampCss(4, 't')};
            left: ${clampCss(-14, 't', true)};
            position: absolute;
            top: ${clampCss(5, 't')};
            width: ${clampCss(4, 't')};
        }
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});

        &::after {
            height: max(4px, ${pxToVw(4, 'd')});
            left: max(${pxToVw(-14, 'd')}, -14px);
            top: max(5px, ${pxToVw(5, 'd')});
            width: max(4px, ${pxToVw(4, 'd')});
        }
    }
`;

const SubLink = styled(Link)`
    && {
        ${FONTS.REGULAR}
    }
`;